import { useMemo } from "react";
import { SortType } from "rsuite-table";

import { Executor } from "../../../../services";
import { useTypedSelector } from "../../../../redux/store";
import { useExecutorsSubHandler } from "../../../../hooks";
import { useFilteredExecutors } from "../../hook";

type Sort = {
	dataKey?: string;
	sortType?: SortType;
};

/**
 * Full executor widget list from the global FRONT subscribe.
 * No `limit` — pagination belongs to the table window, not this consumer.
 */
const useExecutorWidgetModels = (query: string, sort?: Sort) => {
	const { getAllExecutorsSubHandler } = useExecutorsSubHandler();
	const { criteria } = useTypedSelector(
		(state) => state.orders.executors.filter,
	);

	const onlineStatus = useMemo(() => {
		const online = criteria.online.includes("online");
		const offline = criteria.online.includes("offline");

		if (online && offline) return undefined;
		if (online) return true;
		if (offline) return false;
		return undefined;
	}, [criteria.online]);

	const workingStatus = useMemo(() => {
		const working = criteria.workingStatus.includes("working");
		const notWorking = criteria.workingStatus.includes("notWorking");

		if (working && notWorking) return undefined;
		if (working) return true;
		if (notWorking) return false;
		return undefined;
	}, [criteria.workingStatus]);

	const executorsSubscribe = useMemo(() => {
		const options: Executor.SubscribeOptions = {
			query,
			isOnline: onlineStatus,
			workingStatus,
			statuses: criteria.statuses,
			taxiServiceIds: criteria.taxiServiceIds,
		};

		if (sort?.dataKey && sort.sortType) {
			options.order = {
				[sort.dataKey as keyof Executor.SubscribeOptions["order"]]:
					sort.sortType,
			};
		}

		return getAllExecutorsSubHandler(options);
	}, [
		criteria.statuses,
		criteria.taxiServiceIds,
		getAllExecutorsSubHandler,
		onlineStatus,
		query,
		sort?.dataKey,
		sort?.sortType,
		workingStatus,
	]);

	const filteredExecutors = useFilteredExecutors(
		executorsSubscribe,
		{
			companyIds: criteria.companyIds,
			taxiServiceIds: criteria.taxiServiceIds,
			executorGroupIds: criteria.executorGroupIds,
			fleetIds: criteria.fleetIds,
		},
		{
			companyIds: true,
			taxiServiceIds: true,
			executorGroupIds: true,
			fleetIds: true,
		},
	);

	return { executorsSubscribe, filteredExecutors };
};

export default useExecutorWidgetModels;
